// Thin wrapper around the two backend endpoints this app uses (see
// ../../backend/server.mjs). Same-origin in both dev (Vite proxy, vite.config.ts)
// and production (Apache proxies /api/, see deploy/), so plain fetch is enough — no
// base URL configuration needed.

export interface UnresolvedEntry {
  qty: number;
  name: string;
  category: string;
  reason: 'not-found' | 'no-image';
}

export interface DeckReport {
  deckTitle: string;
  totalCardsParsed: number;
  totalCardsPlaced: number;
  pagesGenerated: number;
  grid: { cols: number; rows: number };
  looseMatchCount: number;
  unresolved: UnresolvedEntry[];
  parseWarnings: string[];
  categoryWarnings: string[];
}

export interface Meta {
  totalCards: number;
  maxCardsPerRequest: number;
}

export class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function fetchMeta(): Promise<Meta> {
  const res = await fetch('/api/meta');
  if (!res.ok) throw new ApiError('Impossibile contattare il server.');
  return res.json();
}

export async function generateDeckPdf(
  decklist: string,
): Promise<{ blob: Blob; filename: string; report: DeckReport }> {
  const res = await fetch('/api/generate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decklist }),
  });

  if (!res.ok) {
    let message = `Errore del server (${res.status}).`;
    try {
      const body = await res.json();
      if (body?.error) message = body.error;
    } catch {
      // response wasn't JSON (e.g. a proxy error page) — keep the generic message
    }
    throw new ApiError(message);
  }

  const reportHeader = res.headers.get('X-Deck-Report');
  const report: DeckReport = reportHeader
    ? JSON.parse(decodeURIComponent(reportHeader))
    : {
        deckTitle: 'deck',
        totalCardsParsed: 0,
        totalCardsPlaced: 0,
        pagesGenerated: 0,
        grid: { cols: 0, rows: 0 },
        looseMatchCount: 0,
        unresolved: [],
        parseWarnings: [],
        categoryWarnings: [],
      };

  const disposition = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="([^"]+)"/.exec(disposition);
  const filename = filenameMatch?.[1] ?? 'deck.pdf';

  const blob = await res.blob();
  return { blob, filename, report };
}
